import { styled } from '@mui/material/styles'
import Box from '@/components/common/Box'
import {
	colors,
	typography,
	spacing,
	radius,
	sizing,
	focusVisible,
	breakpoints,
	transitions,
} from '@/styles/designTokens'

export const HeaderRoot = styled('header')(({ theme }) => ({
	display: 'flex',
	flexDirection: 'column',
	width: '100%',
	borderBottom: `1px solid ${colors.borderSubtle}`,
	[theme.breakpoints.up('lg')]: {
		borderBottom: 'none',
	},
	[`@media (max-width: ${breakpoints.compact}px)`]: {
		maxWidth: breakpoints.compact,
		overflow: 'hidden',
	},
}))

// ── Expanded header (full layout) ──────────────────────────────────────────
// Outer grid row: animates 1fr → 0fr so the collapse tracks the real content
// height exactly (no max-height overestimate). Inner wrapper needs min-height:0
// for the grid row trick to work.

export const ExpandedOuter = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'isCompact',
})<{ isCompact?: boolean }>(({ isCompact }) => ({
	display: 'grid',
	gridTemplateRows: isCompact ? '0fr' : '1fr',
	transition: `grid-template-rows ${transitions.spatial}`,
}))

export const ExpandedContent = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'isCompact',
})<{ isCompact?: boolean }>(({ theme, isCompact }) => ({
	display: 'flex',
	flexDirection: 'column',
	minHeight: 0,
	overflow: 'hidden',
	width: '100%',
	paddingTop: isCompact ? 0 : spacing.lg,
	paddingBottom: isCompact ? 0 : spacing.lg,
	paddingLeft: spacing.lg,
	paddingRight: spacing.lg,
	gap: isCompact ? 0 : spacing.lg,
	opacity: isCompact ? 0 : 1,
	pointerEvents: isCompact ? 'none' : 'auto',
	transition: [
		`opacity ${transitions.opacity}`,
		`padding-top ${transitions.spatial}`,
		`padding-bottom ${transitions.spatial}`,
		`gap ${transitions.spatial}`,
	].join(', '),
	[theme.breakpoints.up('md')]: {
		maxWidth: sizing.tabletContentMaxWidth,
		marginLeft: 'auto',
		marginRight: 'auto',
	},
	[theme.breakpoints.up('lg')]: {
		maxWidth: 'none',
		marginLeft: 0,
		marginRight: 0,
		paddingLeft: spacing.xs,
		paddingRight: spacing.xs,
	},
}))

export const HeaderRow = styled(Box)(({ theme }) => ({
	display: 'flex',
	alignItems: 'flex-start',
	gap: spacing.lg,
	width: '100%',
	[theme.breakpoints.up('md')]: {
		alignItems: 'center',
	},
}))

export const BrandingBox = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'hasSignIn',
})<{ hasSignIn?: boolean }>(({ theme, hasSignIn }) => ({
	flex: 1,
	minWidth: 0,
	overflow: 'visible',
	// Cap width only when the sign-in button is present — lifts at lg (1025px) when desktop sidebar begins
	...(hasSignIn && {
		[theme.breakpoints.between('md', 'lg')]: {
			maxWidth: sizing.brandingBoxMaxWidth,
		},
	}),
}))

export const LocationName = styled('p')(({ theme }) => ({
	margin: 0,
	fontWeight: typography.fontWeightBold,
	fontSize: typography.fontSizeLg,
	lineHeight: typography.lineHeightLg,
	letterSpacing: typography.letterSpacingNormal,
	color: colors.textPrimary,
	whiteSpace: 'normal',
	overflowWrap: 'break-word',
	wordBreak: 'break-word',
	[theme.breakpoints.up('md')]: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	[theme.breakpoints.up('lg')]: {
		fontSize: typography.fontSizeXl,
		lineHeight: typography.lineHeightXl,
		letterSpacing: typography.letterSpacingNormalLg,
	},
}))

export const LocationAddress = styled('p')({
	margin: 0,
	fontWeight: typography.fontWeightRegular,
	fontSize: typography.fontSizeMd,
	lineHeight: typography.lineHeightMd,
	letterSpacing: typography.letterSpacingWide,
	color: colors.textAddress,
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
})

export const SchedulePillDesktopWrap = styled(Box)(({ theme }) => ({
	display: 'none',
	alignItems: 'center',
	gap: spacing.sm,
	[theme.breakpoints.up('lg')]: {
		display: 'flex',
		height: sizing.buttonHeight,
	},
}))

export const SchedulePillMobileWrap = styled(Box)(({ theme }) => ({
	display: 'flex',
	width: '100%',
	[theme.breakpoints.up('lg')]: {
		display: 'none',
	},
}))

export const SignInButton = styled('button')(({ theme }) => ({
	display: 'inline-flex',
	[theme.breakpoints.down('md')]: { display: 'none' },
	marginLeft: 'auto',
	alignItems: 'center',
	height: sizing.buttonHeight,
	gap: spacing.sm,
	minWidth: sizing.signInButtonMinWidth,
	padding: `${spacing.sm}px ${spacing.xl}px ${spacing.sm}px ${spacing.lg}px`,
	borderRadius: radius.pill,
	border: 'none',
	outline: `1px solid ${colors.border}`,
	outlineOffset: 0,
	backgroundColor: colors.surfaceWhite,
	cursor: 'pointer',
	fontFamily: 'inherit',
	flexShrink: 0,
	'&:hover': {
		outline: `1px solid ${colors.textPrimary}`,
	},
	'&:focus-visible': {
		outline: `${focusVisible.outline} ${colors.overlayHeavy}`,
		outlineOffset: focusVisible.outlineOffset,
	},
}))

export const SignInButtonText = styled('span')({
	fontWeight: typography.fontWeightBold,
	fontSize: typography.fontSizeMd,
	lineHeight: typography.lineHeightMd,
	color: colors.textPrimary,
})

export const SignInIconButton = styled('button')(({ theme }) => ({
	display: 'none',
	[theme.breakpoints.down('md')]: { display: 'inline-flex' },
	marginLeft: 'auto',
	alignItems: 'center',
	justifyContent: 'center',
	width: sizing.signInIconButton,
	height: sizing.signInIconButton,
	borderRadius: radius.circle,
	border: `1px solid ${colors.border}`,
	backgroundColor: colors.surfaceWhite,
	color: colors.textPrimary,
	cursor: 'pointer',
	flexShrink: 0,
	'&:hover': {
		borderColor: colors.textPrimary,
	},
	'&:focus-visible': {
		outline: `${focusVisible.outline} ${colors.overlayHeavy}`,
		outlineOffset: focusVisible.outlineOffset,
	},
}))

export const SignInIconImg = styled('img')({
	width: sizing.navIcon,
	height: sizing.navIcon,
})

export const AccountIconButton = styled('button')({
	display: 'inline-flex',
	marginLeft: 'auto',
	alignItems: 'center',
	justifyContent: 'center',
	flexShrink: 0,
	padding: 0,
	width: sizing.signInIconButton,
	height: sizing.signInIconButton,
	border: `1px solid ${colors.border}`,
	borderRadius: radius.circle,
	backgroundColor: 'transparent',
	cursor: 'pointer',
	'& .MuiAvatar-root': {
		backgroundColor: colors.surfaceWhite,
		fontSize: typography.fontSizeSm,
		fontWeight: typography.fontWeightBold,
		color: colors.textSecondary,
	},
	'&:hover': {
		borderColor: colors.textPrimary,
	},
	'&:focus-visible': {
		outline: `${focusVisible.outline} ${colors.overlayHeavy}`,
		outlineOffset: focusVisible.outlineOffset,
	},
})

// ── Compact header (scrolled state) ────────────────────────────────────────

export const CompactOuter = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'isCompact',
})<{ isCompact?: boolean }>(({ isCompact }) => ({
	display: 'grid',
	gridTemplateRows: isCompact ? '1fr' : '0fr',
	transition: `grid-template-rows ${transitions.spatial}`,
}))

export const CompactContent = styled(Box, {
	shouldForwardProp: (prop) => prop !== 'isCompact',
})<{ isCompact?: boolean }>(({ theme, isCompact }) => ({
	display: 'flex',
	alignItems: 'center',
	minHeight: 0,
	overflow: 'hidden',
	width: '100%',
	gap: spacing.lg,
	paddingTop: isCompact ? spacing.lg : 0,
	paddingBottom: isCompact ? spacing.lg : 0,
	paddingLeft: spacing.lg,
	paddingRight: spacing.lg,
	opacity: isCompact ? 1 : 0,
	pointerEvents: isCompact ? 'auto' : 'none',
	transition: [
		`opacity ${transitions.opacity}`,
		`padding-top ${transitions.spatial}`,
		`padding-bottom ${transitions.spatial}`,
	].join(', '),
	[theme.breakpoints.up('md')]: {
		maxWidth: sizing.tabletContentMaxWidth,
		marginLeft: 'auto',
		marginRight: 'auto',
	},
	[theme.breakpoints.up('lg')]: {
		maxWidth: 'none',
		marginLeft: 0,
		marginRight: 0,
		paddingLeft: spacing.xs,
		paddingRight: spacing.xs,
	},
}))
